/**
 * Frontend carousel behavior.
 *
 * Handles auto-rotation, swipe/drag, keyboard arrows, progress bar navigation,
 * and infinite looping with peek-through adjacent slides.
 */
( function () {
	'use strict';

	document.querySelectorAll( '.hwbw-carousel' ).forEach( initCarousel );

	function initCarousel( el ) {
		const track      = el.querySelector( '.hwbw-carousel__track' );
		const slides     = Array.from( el.querySelectorAll( '.hwbw-carousel__slide' ) );
		const progress   = el.querySelector( '.hwbw-carousel__progress' );
		const bar        = el.querySelector( '.hwbw-carousel__progress-bar' );
		const prevBtn    = el.querySelector( '.hwbw-carousel__arrow--prev' );
		const nextBtn    = el.querySelector( '.hwbw-carousel__arrow--next' );
		const playPause  = el.querySelector( '.hwbw-carousel__playpause' );

		if ( slides.length < 1 ) return;

		const autoplaySpeed   = parseInt( el.dataset.autoplaySpeed, 10 ) || 5000;
		const transitionSpeed = parseInt( el.dataset.transitionSpeed, 10 ) || 500;
		const total           = slides.length;

		let current     = 0;
		let autoplayId  = null;
		let isAnimating = false;
		let userPaused  = false;

		// ── Helpers ──────────────────────────────────────────────────────────

		// Shortest wraparound offset from `from` to `to`.
		function wrapOffset( from, to ) {
			let diff = to - from;
			if ( diff > total / 2 ) diff -= total;
			if ( diff < -total / 2 ) diff += total;
			return diff;
		}

		// ── Positioning ──────────────────────────────────────────────────────
		// Adjacent slides show their leading/trailing edge.
		// Infinite loop: every slide is positioned relative to current using
		// the shortest wraparound distance so it always rotates smoothly.

		function positionSlides( animate ) {
			const speed = animate ? transitionSpeed : 0;

			slides.forEach( ( slide, i ) => {
				const offset = wrapOffset( current, i );
				slide.style.transition = speed ? `transform ${ speed }ms cubic-bezier(.4,0,.2,1), opacity ${ speed }ms ease` : 'none';

				// Active slide is position:relative (in-flow, sizes the track), no transform needed.
				// Adjacent/far slides are position:absolute, shifted via translateX.
				if ( offset === 0 ) {
					slide.style.transform = 'none';
				} else {
					// 100% = full slide width, plus 0.5rem gap per position
					slide.style.transform = `translateX(calc(${ offset * 100 }% + ${ offset * 0.5 }rem))`;
				}

				// Active, adjacent, or far
				slide.classList.toggle( 'hwbw-carousel__slide--active', offset === 0 );
				slide.classList.toggle( 'hwbw-carousel__slide--prev', offset === -1 );
				slide.classList.toggle( 'hwbw-carousel__slide--next', offset === 1 );

				// Hide non-active slides from AT and remove their links from tab order.
				if ( offset === 0 ) {
					slide.removeAttribute( 'aria-hidden' );
					slide.inert = false;
				} else {
					slide.setAttribute( 'aria-hidden', 'true' );
					slide.inert = true;
				}

				if ( offset === 0 ) {
					slide.style.opacity = '1';
					slide.style.filter  = 'none';
					slide.style.zIndex  = '2';
				} else if ( Math.abs( offset ) === 1 ) {
					slide.style.opacity = '0.5';
					slide.style.filter  = 'brightness(0.7)';
					slide.style.zIndex  = '1';
				} else {
					slide.style.opacity = '0';
					slide.style.filter  = 'brightness(0.5)';
					slide.style.zIndex  = '0';
				}
			} );

			// Update progress bar
			if ( bar ) {
				const segmentWidth = 100 / total;
				bar.style.width = segmentWidth + '%';
				bar.style.left  = ( current * segmentWidth ) + '%';
			}

			// Update live region
			el.setAttribute( 'aria-live', autoplayId ? 'off' : 'polite' );
		}

		function goTo( index, animate = true ) {
			if ( isAnimating ) return;
			isAnimating = true;

			// Wrap around (no jump — continuous)
			current = ( ( index % total ) + total ) % total;
			positionSlides( animate );

			setTimeout( () => { isAnimating = false; }, animate ? transitionSpeed : 0 );
		}

		function next() { goTo( current + 1 ); }
		function prev() { goTo( current - 1 ); }

		// ── Autoplay ─────────────────────────────────────────────────────────

		function startAutoplay() {
			if ( userPaused ) return;
			stopAutoplay();
			autoplayId = setInterval( next, autoplaySpeed );
		}

		function stopAutoplay() {
			if ( autoplayId ) {
				clearInterval( autoplayId );
				autoplayId = null;
			}
		}

		el.addEventListener( 'mouseenter', stopAutoplay );
		el.addEventListener( 'mouseleave', startAutoplay );
		el.addEventListener( 'focusin', stopAutoplay );
		el.addEventListener( 'focusout', startAutoplay );

		// ── Play / pause toggle (WCAG 2.2.2) ─────────────────────────────────

		if ( playPause ) {
			const labelPause = playPause.dataset.labelPause || 'Pause slideshow';
			const labelPlay  = playPause.dataset.labelPlay  || 'Play slideshow';

			playPause.addEventListener( 'click', () => {
				userPaused = ! userPaused;
				if ( userPaused ) {
					stopAutoplay();
					playPause.dataset.state = 'paused';
					playPause.setAttribute( 'aria-label', labelPlay );
				} else {
					playPause.dataset.state = 'playing';
					playPause.setAttribute( 'aria-label', labelPause );
					startAutoplay();
				}
			} );
		}

		// ── Arrow buttons ────────────────────────────────────────────────────

		prevBtn.addEventListener( 'click', () => { prev(); resetAutoplay(); } );
		nextBtn.addEventListener( 'click', () => { next(); resetAutoplay(); } );

		function resetAutoplay() {
			stopAutoplay();
			startAutoplay();
		}

		// ── Progress bar click ───────────────────────────────────────────────

		if ( progress ) {
			progress.addEventListener( 'click', ( e ) => {
				const rect  = progress.getBoundingClientRect();
				const ratio = ( e.clientX - rect.left ) / rect.width;
				const index = Math.floor( ratio * total );
				goTo( Math.min( index, total - 1 ) );
				resetAutoplay();
			} );
		}

		// ── Keyboard ─────────────────────────────────────────────────────────

		el.setAttribute( 'tabindex', '0' );
		el.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'ArrowLeft' ) { e.preventDefault(); prev(); resetAutoplay(); }
			if ( e.key === 'ArrowRight' ) { e.preventDefault(); next(); resetAutoplay(); }
		} );

		// ── Touch / mouse drag ───────────────────────────────────────────────

		let startX    = 0;
		let startY    = 0;
		let dragging  = false;
		let deltaX    = 0;
		const threshold = 50;

		function pointerStart( x, y ) {
			startX   = x;
			startY   = y;
			deltaX   = 0;
			dragging = true;
		}

		function pointerMove( x, y ) {
			if ( ! dragging ) return;
			deltaX = x - startX;

			// If vertical movement is greater, let the page scroll
			if ( Math.abs( y - startY ) > Math.abs( deltaX ) ) {
				dragging = false;
				return;
			}
		}

		function pointerEnd() {
			if ( ! dragging ) return;
			dragging = false;
			if ( deltaX > threshold ) { prev(); resetAutoplay(); }
			else if ( deltaX < -threshold ) { next(); resetAutoplay(); }
		}

		// Touch events
		track.addEventListener( 'touchstart', ( e ) => {
			pointerStart( e.touches[ 0 ].clientX, e.touches[ 0 ].clientY );
		}, { passive: true } );

		track.addEventListener( 'touchmove', ( e ) => {
			pointerMove( e.touches[ 0 ].clientX, e.touches[ 0 ].clientY );
		}, { passive: true } );

		track.addEventListener( 'touchend', pointerEnd );

		// Mouse drag
		track.addEventListener( 'mousedown', ( e ) => {
			e.preventDefault();
			pointerStart( e.clientX, e.clientY );
		} );

		document.addEventListener( 'mousemove', ( e ) => {
			pointerMove( e.clientX, e.clientY );
		} );

		document.addEventListener( 'mouseup', pointerEnd );

		// Prevent link click after drag
		track.addEventListener( 'click', ( e ) => {
			if ( Math.abs( deltaX ) > 10 ) {
				e.preventDefault();
			}
		}, true );

		// ── Init ─────────────────────────────────────────────────────────────

		positionSlides( false );
		startAutoplay();

		// Pause when tab not visible
		document.addEventListener( 'visibilitychange', () => {
			document.hidden ? stopAutoplay() : startAutoplay();
		} );
	}
} )();
